/**
 * Thread Intelligence Panel - COGITO Email Intelligence System
 *
 * Left sidebar showing aggregated intelligence from an email thread:
 * - Key People (with roles and email counts)
 * - Organizations
 * - Key Dates (deadlines, meetings)
 * - Financial Figures
 * - Action Items
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Entity type colors (from COGITO)
const ENTITY_COLORS = {
  PERSON: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  ORGANIZATION: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  DATE: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  AMOUNT: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  ACTION: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
};

interface EntityMention {
  name: string;
  entity_type?: string;
  mention_count?: number;
  cogito_id?: string;
  contact_id?: string;
  role?: string;
  email?: string;
}

interface DateMention {
  text: string;
  parsed?: string;
  context?: string;
  is_deadline?: boolean;
}

interface AmountMention {
  value?: number;
  formatted: string;
  currency?: string;
  context?: string;
}

interface ActionItem {
  id?: string;
  text: string;
  assignee?: string;
  due_date?: string;
  status: string;
}

interface ThreadIntelligence {
  thread_id: string;
  message_count: number;
  first_message_at?: string;
  last_message_at?: string;
  people: EntityMention[];
  organizations: EntityMention[];
  dates: DateMention[];
  amounts: AmountMention[];
  action_items: ActionItem[];
  summary?: {
    context?: string;
    key_ask?: string;
    progress?: string;
    overall_sentiment?: string;
  };
  prognosis?: {
    outcome_prediction?: string;
    confidence_score?: number;
    timeline_risk?: string;
    next_suggested_action?: string;
  };
  overall_sentiment?: string;
  avg_priority?: number;
  requires_response?: boolean;
  participants: string[];
}

interface ThreadIntelligencePanelProps {
  threadId: string;
  intelligence?: ThreadIntelligence | null;
  isLoading?: boolean;
  onEntityClick?: (entity: EntityMention, type: string) => void;
  onActionItemToggle?: (actionItem: ActionItem, completed: boolean) => void;
}

export default function ThreadIntelligencePanel({
  threadId: _threadId,
  intelligence,
  isLoading = false,
  onEntityClick,
  onActionItemToggle,
}: ThreadIntelligencePanelProps) {
  const { t } = useTranslation('email');
  void _threadId; // Reserved for future use (refresh, polling)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['people']));
  // Local state for optimistic checkbox updates (key: item index or id, value: toggled status)
  const [localToggles, setLocalToggles] = useState<Record<string, boolean>>({});

  // Reset local toggles when intelligence data refreshes from server
  useEffect(() => {
    setLocalToggles({});
  }, [intelligence]);

  if (isLoading) {
    return (
      <div className="w-64 border-r border-gray-200 bg-gray-50 p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="space-y-2">
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
          </div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="space-y-2">
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!intelligence) {
    return (
      <div className="w-64 border-r border-gray-200 bg-gray-50 p-4">
        <p className="text-sm text-gray-500">{t('intelligence.no_intelligence_available')}</p>
      </div>
    );
  }

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const SectionHeader = ({
    title,
    section,
    count,
    icon,
  }: {
    title: string;
    section: string;
    count: number;
    icon: React.ReactNode;
  }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between px-2 py-2 hover:bg-gray-100 rounded-lg transition-colors"
    >
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm font-medium text-gray-700">{title}</span>
      </div>
      <div className="flex items-center gap-2">
        {count > 0 && (
          <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">
            {count}
          </span>
        )}
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${
            expandedSections.has(section) ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </button>
  );

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="w-64 border-r border-gray-200 bg-gray-50 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-3 border-b border-gray-200 bg-white">
        <h3 className="text-sm font-semibold text-gray-900">{t('intelligence.thread_intelligence')}</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          {intelligence.message_count} {t('thread.messages')}
        </p>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {/* Key People */}
        <div className="bg-white rounded-lg border border-gray-200">
          <SectionHeader
            title={t('intelligence.key_people')}
            section="people"
            count={intelligence.people.length}
            icon={
              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            }
          />
          {expandedSections.has('people') && intelligence.people.length > 0 && (
            <div className="px-2 pb-2 space-y-1">
              {intelligence.people.map((person, idx) => (
                <button
                  key={idx}
                  onClick={() => onEntityClick?.(person, 'PERSON')}
                  className={`w-full flex items-center gap-2 p-2 rounded-lg hover:bg-blue-50 transition-colors text-left ${ENTITY_COLORS.PERSON.bg}`}
                >
                  {/* Avatar with contact indicator */}
                  <div className="relative">
                    <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-medium">
                      {getInitials(person.name)}
                    </div>
                    {/* Contact linked indicator */}
                    {person.contact_id && (
                      <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border border-white flex items-center justify-center">
                        <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{person.name}</p>
                      {/* COGITO badge */}
                      {person.cogito_id && !person.contact_id && (
                        <span className="flex-shrink-0 px-1 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-600 rounded">
                          {t('intelligence.kb')}
                        </span>
                      )}
                    </div>
                    {person.role && (
                      <p className="text-xs text-gray-500 truncate">{person.role}</p>
                    )}
                    {person.email && (
                      <p className="text-xs text-gray-400 truncate">{person.email}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {person.mention_count && person.mention_count > 1 && (
                      <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                        {person.mention_count}
                      </span>
                    )}
                    {/* View contact indicator */}
                    {person.contact_id && (
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Organizations */}
        {intelligence.organizations.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200">
            <SectionHeader
              title={t('intelligence.organizations')}
              section="organizations"
              count={intelligence.organizations.length}
              icon={
                <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              }
            />
            {expandedSections.has('organizations') && (
              <div className="px-2 pb-2 space-y-1">
                {intelligence.organizations.map((org, idx) => (
                  <button
                    key={idx}
                    onClick={() => onEntityClick?.(org, 'ORGANIZATION')}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg hover:bg-purple-50 transition-colors text-left ${ENTITY_COLORS.ORGANIZATION.bg}`}
                  >
                    {/* Company icon with linked indicator */}
                    <div className="relative">
                      <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center text-purple-600 text-xs font-medium">
                        {org.name[0].toUpperCase()}
                      </div>
                      {/* Company linked indicator */}
                      {org.contact_id && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border border-white flex items-center justify-center">
                          <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{org.name}</p>
                        {/* COGITO badge */}
                        {org.cogito_id && !org.contact_id && (
                          <span className="flex-shrink-0 px-1 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-600 rounded">
                            {t('intelligence.kb')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {org.mention_count && org.mention_count > 1 && (
                        <span className="text-xs text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded">
                          {org.mention_count}
                        </span>
                      )}
                      {/* View company indicator */}
                      {org.contact_id && (
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Key Dates */}
        {intelligence.dates.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200">
            <SectionHeader
              title={t('intelligence.key_dates')}
              section="dates"
              count={intelligence.dates.length}
              icon={
                <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              }
            />
            {expandedSections.has('dates') && (
              <div className="px-2 pb-2 space-y-1">
                {intelligence.dates.map((date, idx) => (
                  <div
                    key={idx}
                    className={`p-2 rounded-lg ${ENTITY_COLORS.DATE.bg} ${date.is_deadline ? 'border-l-2 border-orange-500' : ''}`}
                  >
                    <p className="text-sm font-medium text-gray-900">{date.text}</p>
                    {date.context && (
                      <p className="text-xs text-gray-500 mt-0.5">{date.context}</p>
                    )}
                    {date.is_deadline && (
                      <span className="inline-block mt-1 text-xs text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded">
                        {t('intelligence.deadline')}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Financial Figures */}
        {intelligence.amounts.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200">
            <SectionHeader
              title={t('intelligence.financial_figures')}
              section="amounts"
              count={intelligence.amounts.length}
              icon={
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
            {expandedSections.has('amounts') && (
              <div className="px-2 pb-2 space-y-1">
                {intelligence.amounts.map((amount, idx) => (
                  <div key={idx} className={`p-2 rounded-lg ${ENTITY_COLORS.AMOUNT.bg}`}>
                    <p className="text-sm font-semibold text-green-700">{amount.formatted}</p>
                    {amount.context && (
                      <p className="text-xs text-gray-500 mt-0.5">{amount.context}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action Items */}
        {intelligence.action_items.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200">
            <SectionHeader
              title={t('intelligence.action_items')}
              section="actions"
              count={intelligence.action_items.filter(a => a.status === 'pending').length}
              icon={
                <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              }
            />
            {expandedSections.has('actions') && (
              <div className="px-2 pb-2 space-y-1">
                {intelligence.action_items.map((item, idx) => {
                  const itemKey = item.id || `idx-${idx}`;
                  // Use local toggle if set, otherwise use server status
                  const isCompleted = localToggles[itemKey] !== undefined
                    ? localToggles[itemKey]
                    : item.status === 'completed';

                  return (
                    <div
                      key={idx}
                      className={`flex items-start gap-2 p-2 rounded-lg ${
                        isCompleted ? 'bg-gray-50' : ENTITY_COLORS.ACTION.bg
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isCompleted}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          // Optimistic update - immediate visual feedback
                          setLocalToggles(prev => ({ ...prev, [itemKey]: newValue }));
                          // Call parent handler for persistence
                          onActionItemToggle?.(item, newValue);
                        }}
                        className="mt-0.5 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500 cursor-pointer"
                      />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${isCompleted ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                          {item.text}
                        </p>
                        {item.due_date && (
                          <p className="text-xs text-gray-500 mt-0.5">{t('intelligence.due')} {item.due_date}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Response Indicator */}
      {intelligence.requires_response && (
        <div className="p-2 border-t border-gray-200 bg-amber-50">
          <div className="flex items-center gap-2 text-amber-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="text-xs font-medium">{t('dashboard.response_required')}</span>
          </div>
        </div>
      )}
    </div>
  );
}
